<template>
    <modal @close="close" dialog="modal-lg" class="payment-links-modal dark-top">
        <div slot="title">
            <div class="d-flex justify-content-center align-items-center mb-1">
                <h2 class="m-0">Payment Link Details</h2>
                <span v-if="paymentLink" class="ml-2 lightest px-2 py-1 fs-normal radius-normal" :class="statusConfig.cssClass">{{ statusConfig.label }}</span>
            </div>
            <a v-if="paymentLink" class="reservation-link pointer" :href="reservationUrl" target="_blank">Booking Nº: <span class="reservation-link__number">{{ paymentLink.reservation }}</span></a>
        </div>

        <div slot="body" class="bg-lighter">
            <div v-if="confirmAction" class="w-100 px-4 py-5 bg-lightest text-center">
                <p class="fs-large font-weight-600">
                    <span v-if="confirmAction === 'cancel'">Cancel the Payment Link?</span>
                    <span v-if="confirmAction === 'save'">Confirm the changes to the Payment Link?</span>
                </p>
            </div>

            <div v-else class="w-100 px-4 py-4 bg-lightest">
                <div class="content-wrapper">
                    <div class="form-col">
                        <label>Amount <span class="color-6">*</span></label>
                        <div class="d-flex w-100" v-tooltip="!canEditValue ? 'The amount cannot be changed for Multibanco payments.' : ''">
                            <input
                                type="text"
                                class="fs-normal bg-lighter glued-right input-amount-width"
                                :class="{ 'input--readonly': isReadonly }"
                                v-model="form.value"
                                placeholder="0"
                                v-numFloat
                                :readonly="isReadonly || !canEditValue"
                            >
                            <div class="border-light d-flex justify-content-center glued-left fs-normal currency-width">€</div>
                        </div>
                        <span v-if="isValueExceedingDue" class="fs-small color-6 mt-1 d-block error-message">The payment amount cannot exceed the amount due.</span>
                    </div>

                    <div class="form-col">
                        <label>Expiration date <span class="color-6">*</span></label>
                        <div class="expiration-container">
                            <template v-if="expirationDatePart">
                                <input type="text" class="expiration-date-width" :value="expirationDatePart" readonly>
                                <div class="expiration-separator"></div>
                                <input type="text" class="expiration-time-width text-center" :value="expirationTimePart ? expirationTimePart + ' h' : ''" readonly>
                            </template>
                            <span v-else>No expiration date</span>
                        </div>
                    </div>

                    <div v-if="isLinkVisible" class="form-col form-col--full">
                        <label>Payment Link URL</label>
                        <div class="d-flex w-100">
                            <input type="text" class="flex-grow-1 bg-lighter" :value="paymentLink && paymentLink.url" readonly>
                            <span class="dark border-dark radius-normal d-flex align-items-center px-2 py-1 pointer bg-lightest fs-smaller ml-2" @click="copyUrl">
                                <i class="ico ico-notes fs-smaller"></i>
                                Copy
                            </span>
                        </div>
                    </div>

                    <div class="form-col form-col--full">
                        <label>Notes</label>
                        <input type="text" class="w-100" v-model="form.description" :readonly="isReadonly" :class="{ 'input--readonly': isReadonly }" maxlength="50">
                        <span v-if="!isReadonly" class="fs-small mt-1 d-block text-right" :class="{ 'color-6': (form.description || '').length > 50 }">{{ (form.description || '').length }}/50</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="confirmAction" slot="footer">
            <button type="button" class="btn cancel widest" @click="confirmAction = null">Back</button>
            <button type="button" class="btn ok widest" :disabled="isSaving" @click="executeConfirm">Confirm</button>
        </div>

        <div v-else slot="footer">
            <button v-if="isPending" type="button" class="btn action" @click="confirmAction = 'cancel'">Cancel Payment</button>
            <button v-if="isPending" type="button" class="btn ok" :disabled="!hasChanges || isValueExceedingDue" @click="confirmAction = 'save'">Update Payment</button>
        </div>
    </modal>
</template>

<script>
    import { mapActions } from 'vuex'
    import moment from 'moment'
    import { eventHub } from '@/main'

    import Modal from './Modal'
    import paymentLinksService from '@/api/reservation/paymentLinksService'
    import PAYMENT_LINK_STATUS from '@/enums/paymentLinkStatus'

    export default {
        data() {
            return {
                paymentLink: null,
                form: {
                    value: null,
                    description: ''
                },
                originalForm: {
                    value: null,
                    description: ''
                },
                confirmAction: null,
                canEditValue: true,
                isSaving: false
            }
        },
        created() {
            eventHub.$on('paymentLinks:detailsLoaded', (paymentLink) => {
                this.paymentLink = paymentLink;
                this.form.value = paymentLink.value;
                this.form.description = paymentLink.description || '';
                this.canEditValue = paymentLink.canEditValue || true;
                this.originalForm.value = paymentLink.value;
                this.originalForm.description = paymentLink.description || '';
            });
        },
        beforeDestroy() {
            eventHub.$off('paymentLinks:detailsLoaded');
        },
        computed: {
            expirationDatePart() {
                if (!this.paymentLink) return '';
                const value = this.paymentLink.cancelBookingIfNotPaidAfter;
                return value ? moment(value).format('DD.MM.YYYY') : '';
            },
            expirationTimePart() {
                if (!this.paymentLink) return '';
                const value = this.paymentLink.cancelBookingIfNotPaidAfter;
                return value ? moment(value).format('HH:mm') : '';
            },
            dueToPayDisplay() {
                if (!this.paymentLink || this.paymentLink.due == null) return '0.00 €';
                return `${this.paymentLink.due.toFixed(2)} €`;
            },
            isDuePaid() {
                if (!this.paymentLink || this.paymentLink.due == null) return true;
                return this.paymentLink.due == 0;
            },
            totalDisplay() {
                if (!this.paymentLink) return '0.00 €';
                const total = this.paymentLink.billingTotal != null ? this.paymentLink.billingTotal : 0;
                return `${total.toFixed(2)} €`;
            },
            reservationUrl() {
                return `/reservations/view/${this.paymentLink.reservation.replace('#', '')}`;
            },
            isPending() {
                return this.paymentLink && this.paymentLink.status === PAYMENT_LINK_STATUS.PENDING;
            },
            isReadonly() {
                return !this.isPending;
            },
            hasChanges() {
                return (this.form.description !== this.originalForm.description
                    || parseFloat(this.form.value) !== parseFloat(this.originalForm.value))
                    && !this.isDescriptionExceedingLimit;
            },
            isValueExceedingDue() {
                if (!this.isPending || this.paymentLink.due == null) return false;
                return parseFloat(this.form.value) > this.paymentLink.due;
            },
            isDescriptionExceedingLimit() {
                return this.form.description.length > 50;
            },
            isLinkVisible() {
                if (!this.paymentLink) return false;
                return this.paymentLink.status !== PAYMENT_LINK_STATUS.PAID
                    && this.paymentLink.status !== PAYMENT_LINK_STATUS.CANCELLED;
            },
            statusConfig() {
                const STATUS_CONFIG = {
                    [PAYMENT_LINK_STATUS.PENDING]: { label: 'Pending', cssClass: 'bg-color-1' },
                    [PAYMENT_LINK_STATUS.PAID]: { label: 'Paid', cssClass: 'bg-color-3' },
                    [PAYMENT_LINK_STATUS.CANCELLED]: { label: 'Canceled', cssClass: 'bg-color-6' },
                    [PAYMENT_LINK_STATUS.EXPIRED]: { label: 'Expired', cssClass: 'bg-color-5' },
                };
                return STATUS_CONFIG[this.paymentLink.status] || {};
            }
        },
        methods: {
            ...mapActions(['closeModal']),
            close() {
                this.closeModal();
            },
            copyUrl() {
                if (!this.paymentLink || !this.paymentLink.url) return;
                this.$clipboard(this.paymentLink.url);
                this.$toasted.success('Url copied');
            },
            async executeConfirm() {
                this.isSaving = true;

                if (this.confirmAction === 'save') {
                    await this.saveChanges();
                } else if (this.confirmAction === 'cancel') {
                    await this.cancelPaymentLink();
                }

                this.isSaving = false;
            },
            async cancelPaymentLink() {
                const result = await paymentLinksService.cancelPaymentLink(this.paymentLink.id);

                if (!result.ok) return;

                this.$toasted.success('Payment Link successfully canceled');
                this.close();
                eventHub.$emit('paymentLinks:refresh');
            },
            async saveChanges() {
                const payload = {
                    value: parseFloat(this.form.value),
                    description: this.form.description
                };

                const result = await paymentLinksService.updatePaymentLink(this.paymentLink.id, payload);

                if (!result.ok) return;

                this.$toasted.success('Payment link successfully updated');
                this.close();
                eventHub.$emit('paymentLinks:refresh');
            }
        },
        components: {
            Modal
        }
    }
</script>

<style lang="scss" scoped>
    .form-col {
        min-width: 0;
    }

    .form-col--full {
        grid-column: 1 / -1;
    }

    .input-amount-width {
        flex: 1;
    }

    .input--readonly {
        cursor: not-allowed;
    }

    .error-message {
        line-height: 1.2;
    }

    .currency-width {
        width: 36px;
        padding: 5px 8px;
    }

    .expiration-date-width {
        flex: 2;
        min-width: 100px;
    }

    .expiration-time-width {
        flex: 1;
        min-width: 60px;
        text-align: center;
    }

    .expiration-container {
        display: flex;
        align-items: center;
        width: 100%;
        border: 1px solid #DBDFE2;
        border-radius: 4px;
        background-color: #f5f8fa;
        overflow: hidden;

        input {
            border: none;
            background: transparent;
            margin: 0;
            padding: 5px 10px;
        }

        span {
            padding: 5px 12px;
            white-space: nowrap;
            color: #000;
            font-weight: 400;
        }
    }

    .expiration-separator {
        align-self: stretch;
        width: 1px;
        background-color: #DBDFE2;
    }


.content-wrapper {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px 24px;
        justify-content: center;
        width: 100%;

        input[type="text"] {
            padding-top: 5px;
            padding-bottom: 5px;
        }
    }

    .payment-summary {
        border: 1px solid #DBDFE2;
        background-color: #f5f8fa;
        width: 100%;
        border-radius: 4px;
        overflow: hidden;

        label {
            min-width: 80px;
        }

        span {
            white-space: nowrap;
        }

        &__row {
            padding: 8px 12px;

            & + & {
                border-top: 1px solid #DBDFE2;
            }
        }
    }

    .reservation-link {
        font-size: 0.8em;
        color: #FFF;
        text-decoration: none;

        .reservation-link__number {
            background-color: #8ca0b3;
            padding: 0.2em 0.5em;
            margin: 0 3px;
            border-radius: 5px;
            text-decoration: underline;
        }
    }
</style>

<style lang="scss">
    .payment-links-modal.dark-top .modal-header {
        padding-top: 16px;
        padding-bottom: 16px;
    }

    .payment-links-modal .modal-lg {
        max-width: 700px;
    }
</style>


// WEBPACK FOOTER //
// PaymentLinkDetails.vue?ddf48948